import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import ListRoundedIcon from '@mui/icons-material/ListRounded';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { StudyReportTocItem } from './StudyReportSectionNavigation';
import StudyReportSectionNumberBadge from './StudyReportSectionNumberBadge';

export interface ScrollNavSection {
  id: string;
  title: string;
  numberLabel: string;
}

interface ScrollNavAttachmentInsets {
  top: number;
  left: number;
  right: number;
}

interface Props {
  visible: boolean;
  reportTitle: string;
  sections: ScrollNavSection[];
  tocItems: StudyReportTocItem[];
  activeSectionId: string | null;
  onScrollToSection: (sectionId: string) => void;
  onScrollToTop: () => void;
  attachmentInsets: ScrollNavAttachmentInsets;
}

const StudyReportScrollNavHeader = React.forwardRef<HTMLDivElement, Props>(
  (
    { visible, reportTitle, sections, tocItems, activeSectionId, onScrollToSection, onScrollToTop, attachmentInsets },
    ref
  ) => {
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
    const sectionMenuItemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

    const activeSectionIndex = React.useMemo(
      () => sections.findIndex((s) => s.id === activeSectionId),
      [sections, activeSectionId]
    );
    const effectiveActiveSectionIndex = React.useMemo(() => {
      if (sections.length === 0) {
        return -1;
      }

      return activeSectionIndex >= 0 ? activeSectionIndex : 0;
    }, [activeSectionIndex, sections]);
    const activeSection = effectiveActiveSectionIndex >= 0 ? sections[effectiveActiveSectionIndex] : null;

    const handleMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchor(event.currentTarget);
    }, []);

    const handleMenuClose = React.useCallback(() => {
      setMenuAnchor(null);
    }, []);

    const handleSectionClick = React.useCallback(
      (sectionId: string) => {
        handleMenuClose();
        onScrollToSection(sectionId);
      },
      [handleMenuClose, onScrollToSection]
    );

    const handleMenuEnter = React.useCallback(() => {
      if (!activeSectionId) {
        return;
      }

      sectionMenuItemRefs.current.get(activeSectionId)?.scrollIntoView({ block: 'nearest' });
    }, [activeSectionId]);

    const handlePrevSection = React.useCallback(() => {
      if (effectiveActiveSectionIndex > 0) {
        onScrollToSection(sections[effectiveActiveSectionIndex - 1].id);
      }
    }, [effectiveActiveSectionIndex, sections, onScrollToSection]);

    const handleNextSection = React.useCallback(() => {
      if (effectiveActiveSectionIndex >= 0 && effectiveActiveSectionIndex < sections.length - 1) {
        onScrollToSection(sections[effectiveActiveSectionIndex + 1].id);
      }
    }, [effectiveActiveSectionIndex, sections, onScrollToSection]);

    const handleScrollToLast = React.useCallback(() => {
      if (sections.length > 0) {
        onScrollToSection(sections[sections.length - 1].id);
      }
    }, [sections, onScrollToSection]);

    if (!visible) {
      return null;
    }

    const horizontalInsetWidth = attachmentInsets.left + attachmentInsets.right;
    const horizontalTransform = attachmentInsets.left ? `translateX(-${attachmentInsets.left}px)` : 'translateX(0)';
    const verticalTransform = visible ? 'translateY(0)' : 'translateY(-8px)';

    return (
      <Stack
        ref={ref}
        direction="row"
        alignItems="center"
        data-testid="study-report-scroll-nav"
        data-visible={visible}
        sx={{
          position: 'sticky',
          top: attachmentInsets.top ? `-${attachmentInsets.top}px` : 0,
          width: horizontalInsetWidth ? `calc(100% + ${horizontalInsetWidth}px)` : '100%',
          maxWidth: horizontalInsetWidth ? 'none' : '100%',
          minWidth: 0,
          marginLeft: 0,
          marginRight: 0,
          alignSelf: 'stretch',
          boxSizing: 'border-box',
          zIndex: theme.zIndex.appBar,
          ...getTranslucentSurfaceStyles(theme),
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          px: 2,
          py: 0.75,
          minHeight: 40,
          opacity: visible ? 1 : 0,
          transform: `${horizontalTransform} ${verticalTransform}`,
          transition: 'opacity 150ms ease-out, transform 150ms ease-out',
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        {/* Left: report title */}
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flexShrink: 0, minWidth: 80, maxWidth: 260 }}>
          {reportTitle}
        </Typography>

        <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />

        {/* Active section badge */}
        {activeSection && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <StudyReportSectionNumberBadge value={activeSection.numberLabel} size="compact" />
            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
              {activeSection.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              / {sections.length}
            </Typography>
          </Stack>
        )}

        <Box sx={{ flex: 1 }} />

        <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />

        {/* Right: scroll-to-top/bottom, prev/next, TOC menu */}
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <IconButton size="small" onClick={onScrollToTop} aria-label="Scroll to top" sx={{ p: 0.25 }}>
            <KeyboardDoubleArrowUpRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleScrollToLast}
            disabled={sections.length === 0}
            aria-label="Scroll to last section"
            sx={{ p: 0.25 }}
          >
            <KeyboardDoubleArrowDownRoundedIcon fontSize="small" />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.75 }} />

          <IconButton
            size="small"
            onClick={handlePrevSection}
            disabled={effectiveActiveSectionIndex <= 0}
            aria-label="Previous section"
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowUpRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNextSection}
            disabled={effectiveActiveSectionIndex < 0 || effectiveActiveSectionIndex >= sections.length - 1}
            aria-label="Next section"
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowDownRoundedIcon fontSize="small" />
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.75 }} />

          <IconButton size="small" onClick={handleMenuOpen} aria-label="Table of contents" sx={{ p: 0.25 }}>
            <ListRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          disableRestoreFocus
          TransitionProps={{ onEnter: handleMenuEnter }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 280 } } }}
        >
          <List dense disablePadding sx={{ py: 0.5 }}>
            {tocItems.map((item) => {
              const isSectionItem = Boolean(item.sectionId);
              const isSelected = item.sectionId === activeSectionId;
              const isActiveParent =
                !isSectionItem && item.parentId && activeSection?.id?.startsWith(`${item.parentId}:`);

              if (!isSectionItem) {
                return (
                  <Box key={item.key} sx={{ px: 2, pt: 1, pb: 0.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ minWidth: 24 }}>
                        {item.numberLabel}
                      </Typography>
                      <Typography variant="body2" fontWeight={isActiveParent ? 700 : 600} color="text.primary">
                        {item.title}
                      </Typography>
                    </Stack>
                  </Box>
                );
              }

              return (
                <ListItemButton
                  key={item.key}
                  selected={isSelected}
                  onClick={() => handleSectionClick(item.sectionId!)}
                  ref={(element: HTMLDivElement | null) => {
                    if (element && item.sectionId) {
                      sectionMenuItemRefs.current.set(item.sectionId, element);
                    } else if (item.sectionId) {
                      sectionMenuItemRefs.current.delete(item.sectionId);
                    }
                  }}
                  sx={{ pl: item.depth > 0 ? 4 : 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        px: item.numberLabel.length > 2 ? 0.75 : 0,
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isSelected ? 'primary.main' : 'action.selected',
                        color: isSelected ? 'primary.contrastText' : 'text.secondary',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {item.numberLabel}
                    </Box>
                  </ListItemIcon>
                  <ListItemText primary={item.title} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                </ListItemButton>
              );
            })}
          </List>
        </Menu>
      </Stack>
    );
  }
);

StudyReportScrollNavHeader.displayName = 'StudyReportScrollNavHeader';

export default StudyReportScrollNavHeader;
